// src/views/auth/LoginPage.jsx
import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { getApiBaseUrl } from "../../services/apiBase";

function formatarCountdown(seg) {
  if (seg <= 0) return "";
  const m = Math.floor(seg / 60);
  const s = seg % 60;
  if (m > 0) return `${m}m ${String(s).padStart(2,"0")}s`;
  return `${s}s`;
}

export default function LoginPage() {
  const { login } = useAuth();
  const [email,     setEmail]     = useState("");
  const [senha,     setSenha]     = useState("");
  const [lembrar,   setLembrar]   = useState(false);
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [erro,      setErro]      = useState("");
  const [bloqueio,  setBloqueio]  = useState(0); // segundos restantes de 429
  const [modo,      setModo]      = useState("login");
  const [emailReset, setEmailReset] = useState("");
  const [msgReset,  setMsgReset]  = useState("");

  const BASE = getApiBaseUrl();
  const intervalRef = useRef(null);

  // Countdown do rate-limit (429)
  useEffect(() => {
    if (bloqueio <= 0) {
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
      return;
    }
    intervalRef.current = setInterval(() => {
      setBloqueio(s => Math.max(0, s - 1));
    }, 1000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [bloqueio > 0]); // eslint-disable-line

  const handleLogin = async (e) => {
    e.preventDefault();
    if (bloqueio > 0) return;
    if (!email.trim() || !senha.trim()) { setErro("Preencha e-mail e senha."); return; }
    setLoading(true); setErro("");
    try {
      await login(email.trim(), senha, lembrar);
    } catch(err) {
      const msg = err.message || "Erro ao fazer login.";
      if (err.status === 429) {
        const seg = Number(err.retryAfter) || 60;
        setBloqueio(seg);
        setErro(`Muitas tentativas. Tente novamente em ${formatarCountdown(seg)}.`);
      } else if (err.code === "TENANT_BLOCKED" || msg.includes("TENANT_BLOCKED:")) {
        setErro("🔒 " + msg.replace("TENANT_BLOCKED:","").trim());
      } else if (err.code === "TENANT_CANCELLED" || msg.includes("TENANT_CANCELLED:")) {
        setErro("❌ " + msg.replace("TENANT_CANCELLED:","").trim());
      } else {
        setErro(msg);
      }
    }
    setLoading(false);
  };

  const handleEsqueci = async (e) => {
    e.preventDefault();
    if (!emailReset.trim()) { setErro("Informe seu e-mail."); return; }
    setLoading(true); setErro("");
    try {
      const res = await fetch(`${BASE}/auth/esqueci-senha`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ email: emailReset.trim() }),
      });
      let data = {};
      try { data = await res.json(); } catch {}
      if (res.status === 429) {
        const seg = Number(res.headers.get("Retry-After")) || Number(data.retryAfter) || 3600;
        setErro(`Muitas solicitações. Tente novamente em ${formatarCountdown(seg)}.`);
      } else {
        setMsgReset(data.message || "Se o e-mail estiver cadastrado, você receberá as instruções.");
      }
    } catch {
      setErro("Erro ao enviar solicitação.");
    }
    setLoading(false);
  };

  return (
    <div style={{
      minHeight:"100vh", background:"linear-gradient(135deg, #0F2347 0%, #1B3A6B 50%, #2A5298 100%)",
      display:"flex", alignItems:"center", justifyContent:"center", padding:16,
    }}>
      {/* Card */}
      <div style={{
        width:"100%", maxWidth:420, background:"var(--bg-surface)",
        borderRadius:16, boxShadow:"0 24px 64px rgba(0,0,0,.35)",
        overflow:"hidden",
      }}>
        {/* Header */}
        <div style={{
          background:"linear-gradient(135deg, #1B3A6B, #2A5298)",
          padding:"32px 32px 24px", textAlign:"center",
        }}>
          <div style={{
            width:60, height:60, borderRadius:16, background:"rgba(255,255,255,.15)",
            display:"flex", alignItems:"center", justifyContent:"center",
            margin:"0 auto 12px",
          }}>
            <i className="bi bi-buildings-fill" style={{fontSize:28, color:"#fff"}}/>
          </div>
          <div style={{fontSize:24, fontWeight:800, color:"#fff", letterSpacing:"-0.5px"}}>
            Horizon<span style={{color:"#27AE60"}}>eCondo</span>
          </div>
          <div style={{fontSize:13, color:"rgba(255,255,255,.7)", marginTop:4}}>
            {modo==="login" ? "Sistema de Gestão Condominial" : "Recuperar senha"}
          </div>
        </div>

        {/* Body */}
        <div style={{padding:"28px 32px 32px"}}>

          {/* MODO LOGIN */}
          {modo==="login" && (
            <form onSubmit={handleLogin}>
              {bloqueio > 0 ? (
                <div style={{
                  background:"#FEF3C7", border:"1px solid #FDE68A", color:"#92400E", borderRadius:8,
                  padding:"12px 14px", marginBottom:16, fontSize:13,
                  display:"flex", alignItems:"center", gap:10,
                }}>
                  <i className="bi bi-clock-history" style={{fontSize:20}}/>
                  <div style={{flex:1, lineHeight:1.4}}>
                    <div style={{fontWeight:700, marginBottom:2}}>Muitas tentativas de login</div>
                    <div style={{fontSize:12}}>
                      Aguarde <strong>{formatarCountdown(bloqueio)}</strong> antes de tentar novamente.
                    </div>
                  </div>
                </div>
              ) : erro && (
                <div style={{
                  background:"var(--bg-danger-soft)", color:"#991B1B", borderRadius:8,
                  padding:"10px 14px", marginBottom:16, fontSize:13,
                  display:"flex", alignItems:"center", gap:8,
                }}>
                  <i className="bi bi-exclamation-circle-fill"/>
                  {erro}
                </div>
              )}

              <div style={{marginBottom:16}}>
                <label style={{fontSize:13, fontWeight:600, color:"#374151", display:"block", marginBottom:6}}>
                  E-mail
                </label>
                <div style={{position:"relative"}}>
                  <i className="bi bi-envelope" style={{
                    position:"absolute", left:12, top:"50%", transform:"translateY(-50%)",
                    color:"#9CA3AF", fontSize:15,
                  }}/>
                  <input
                    type="email" autoFocus
                    value={email} onChange={e=>{setEmail(e.target.value);setErro("");}}
                    placeholder="seu@email.com.br"
                    style={{
                      width:"100%", padding:"10px 12px 10px 36px", borderRadius:8,
                      border:"1px solid #D1D5DB", fontSize:14, outline:"none",
                      boxSizing:"border-box",
                    }}
                    onFocus={e=>e.target.style.borderColor="#1B3A6B"}
                    onBlur={e=>e.target.style.borderColor="#D1D5DB"}
                  />
                </div>
              </div>

              <div style={{marginBottom:8}}>
                <label style={{fontSize:13, fontWeight:600, color:"#374151", display:"block", marginBottom:6}}>
                  Senha
                </label>
                <div style={{position:"relative"}}>
                  <i className="bi bi-lock" style={{
                    position:"absolute", left:12, top:"50%", transform:"translateY(-50%)",
                    color:"#9CA3AF", fontSize:15,
                  }}/>
                  <input
                    type={mostrarSenha?"text":"password"}
                    value={senha} onChange={e=>{setSenha(e.target.value);setErro("");}}
                    placeholder="••••••••"
                    style={{
                      width:"100%", padding:"10px 40px 10px 36px", borderRadius:8,
                      border:"1px solid #D1D5DB", fontSize:14, outline:"none",
                      boxSizing:"border-box",
                    }}
                    onFocus={e=>e.target.style.borderColor="#1B3A6B"}
                    onBlur={e=>e.target.style.borderColor="#D1D5DB"}
                  />
                  <button type="button"
                    style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",
                      background:"none",border:"none",cursor:"pointer",color:"#9CA3AF",padding:4}}
                    onClick={()=>setMostrarSenha(p=>!p)}>
                    <i className={`bi bi-eye${mostrarSenha?"-slash":""}`}/>
                  </button>
                </div>
              </div>

              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
                <label style={{display:"flex",alignItems:"center",gap:6,fontSize:13,color:"#6B7280",cursor:"pointer"}}>
                  <input type="checkbox" checked={lembrar} onChange={e=>setLembrar(e.target.checked)}
                    style={{accentColor:"#1B3A6B"}}/>
                  Lembrar de mim
                </label>
                <button type="button" onClick={()=>{setModo("esqueci");setErro("");}}
                  style={{background:"none",border:"none",color:"#1B3A6B",fontSize:13,cursor:"pointer",fontWeight:600}}>
                  Esqueci minha senha
                </button>
              </div>

              <button type="submit" disabled={loading || bloqueio > 0}
                style={{
                  width:"100%", padding:"12px", borderRadius:8, border:"none",
                  background: bloqueio > 0
                    ? "linear-gradient(135deg,#9CA3AF,#6B7280)"
                    : "linear-gradient(135deg,#1B3A6B,#2A5298)",
                  color:"#fff", fontSize:15, fontWeight:700,
                  cursor: bloqueio > 0 ? "not-allowed" : "pointer",
                  display:"flex",alignItems:"center",justifyContent:"center",gap:8,
                  opacity:loading?.7:1, transition:"opacity .15s",
                }}>
                {bloqueio > 0
                  ? <><i className="bi bi-lock-fill"/>Aguarde {formatarCountdown(bloqueio)}</>
                  : loading
                    ? <><span className="spinner-border spinner-border-sm"/>Entrando...</>
                    : <><i className="bi bi-box-arrow-in-right"/>Entrar</>
                }
              </button>
            </form>
          )}

          {/* MODO ESQUECI */}
          {modo==="esqueci" && (
            <form onSubmit={handleEsqueci}>
              {!msgReset ? (
                <>
                  <p style={{fontSize:13.5,color:"#6B7280",marginBottom:16}}>
                    Informe seu e-mail cadastrado e enviaremos as instruções para redefinir sua senha.
                  </p>
                  {erro && (
                    <div style={{background:"var(--bg-danger-soft)",color:"#991B1B",borderRadius:8,padding:"10px 14px",marginBottom:14,fontSize:13}}>
                      <i className="bi bi-exclamation-circle-fill me-2"/>{erro}
                    </div>
                  )}
                  <div style={{marginBottom:16}}>
                    <label style={{fontSize:13,fontWeight:600,color:"#374151",display:"block",marginBottom:6}}>E-mail</label>
                    <input type="email" value={emailReset}
                      onChange={e=>{setEmailReset(e.target.value);setErro("");}}
                      placeholder="seu@email.com.br" autoFocus
                      style={{width:"100%",padding:"10px 12px",borderRadius:8,border:"1px solid #D1D5DB",fontSize:14,outline:"none",boxSizing:"border-box"}}/>
                  </div>
                  <button type="submit" disabled={loading}
                    style={{width:"100%",padding:"12px",borderRadius:8,border:"none",
                      background:"linear-gradient(135deg,#1B3A6B,#2A5298)",color:"#fff",
                      fontSize:14,fontWeight:700,cursor:"pointer",marginBottom:12}}>
                    {loading?<span className="spinner-border spinner-border-sm"/>:"Enviar instruções"}
                  </button>
                  <button type="button" onClick={()=>{setModo("login");setErro("");}}
                    style={{width:"100%",padding:"10px",borderRadius:8,border:"1px solid #D1D5DB",
                      background:"var(--bg-surface)",color:"#6B7280",fontSize:13,cursor:"pointer"}}>
                    ← Voltar ao login
                  </button>
                </>
              ) : (
                <>
                  <div style={{textAlign:"center",padding:"16px 0"}}>
                    <i className="bi bi-envelope-check-fill" style={{fontSize:40,color:"#166534",display:"block",marginBottom:12}}/>
                    <p style={{fontSize:14,color:"#166534",fontWeight:600}}>{msgReset}</p>
                  </div>
                  <button type="button" onClick={()=>{setModo("login");setMsgReset("");setEmailReset("");}}
                    style={{width:"100%",padding:"10px",borderRadius:8,border:"1px solid #D1D5DB",
                      background:"var(--bg-surface)",color:"#6B7280",fontSize:13,cursor:"pointer"}}>
                    ← Voltar ao login
                  </button>
                </>
              )}
            </form>
          )}
        </div>

        {/* Footer */}
        <div style={{
          borderTop:"1px solid #F3F4F6", padding:"12px 32px",
          textAlign:"center", fontSize:11.5, color:"#9CA3AF",
        }}>
          Horizon eCondo © {new Date().getFullYear()} — Gestão Condominial by <a href="https://ievoltech.com.br" target="_blank" rel="noopener noreferrer">iEvol Tech</a>
        </div>
      </div>
    </div>
  );
}
